import { create } from 'zustand';
import type { ToastVariant } from '@/design-system/components/feedback/Toast';
import { useAppConfigStore } from '@/store/useAppConfigStore';
import { readJson, readString, writeJson, writeString } from '@/utils/safeLocalStorage';

const CONTRIBUTION_SUGGESTION_COOLDOWN_STORAGE_KEY = 'elbaul.contributionSuggestionCooldowns';
// Configurable vía appsettings (ver useAppConfigStore.contributionSuggestionCooldownMinutes) en
// vez de fijo, para poder acortarlo en pruebas — se lee en cada llamada, no una vez al cargar el
// módulo, así un fetchAppConfig() posterior (la config tarda en llegar del backend) sí se aplica.
function contributionSuggestionCooldownMs(): number {
  return useAppConfigStore.getState().contributionSuggestionCooldownMinutes * 60 * 1000;
}

function readContributionSuggestionCooldowns(): Record<string, number> {
  return readJson(CONTRIBUTION_SUGGESTION_COOLDOWN_STORAGE_KEY, {});
}

function writeContributionSuggestionCooldowns(cooldowns: Record<string, number>): void {
  writeJson(CONTRIBUTION_SUGGESTION_COOLDOWN_STORAGE_KEY, cooldowns);
}

const REMOVAL_REQUESTED_PHOTO_IDS_STORAGE_KEY = 'elbaul.removalRequestedPhotoIds';

function readRemovalRequestedPhotoIds(): string[] {
  return readJson(REMOVAL_REQUESTED_PHOTO_IDS_STORAGE_KEY, []);
}

function writeRemovalRequestedPhotoIds(ids: string[]): void {
  writeJson(REMOVAL_REQUESTED_PHOTO_IDS_STORAGE_KEY, ids);
}

const HAS_LAUNCHED_APP_STORAGE_KEY = 'elbaul.hasLaunchedApp';

// Se calcula una sola vez, al cargar este módulo (arranque de la app) — no en cada mount de
// BaulRoute, porque BaulRoute vuelve a leer/decidir en un efecto cada vez que cambia de baúl
// (ver BaulRoute.tsx) y una función que se pudiera llamar más de una vez marcaría "ya lanzada"
// a mitad de la primerísima sesión, dejando el segundo baúl visitado con un resultado
// distinto al primero. Al ser un `const` de módulo, todo el que importe el store durante esta
// carga de página ve el mismo valor. Si no se puede saber (readString/writeString fallan
// open) no bloqueamos la sugerencia.
const IS_FIRST_APP_LAUNCH = (() => {
  if (readString(HAS_LAUNCHED_APP_STORAGE_KEY)) return false;
  writeString(HAS_LAUNCHED_APP_STORAGE_KEY, '1');
  return true;
})();

interface UIState {
  // Toast state
  showToast: boolean;
  toastMessage: string;
  toastVariant: ToastVariant;
  showToastMessage: (message: string, variant?: ToastVariant) => void;
  hideToast: () => void;

  // Recomendación de contribución (ver ContributionSuggestionContainer): una vez mostrada y
  // resuelta (guardada u "ahora no"), no debe volver a proponerse en ESE baúl durante el
  // cooldown — pero cambiar a otro baúl sí debe poder proponer una sugerencia nueva, de ahí la
  // clave por baulId en vez de un flag global. Persistido en localStorage (sobrevive a cerrar y
  // reabrir la app) pero nunca en backend: es un throttle de cliente, no un estado de dominio.
  isContributionSuggestionOnCooldown: (baulId: string) => boolean;
  startContributionSuggestionCooldown: (baulId: string) => void;

  // Verdadero solo durante la primerísima sesión de este navegador/dispositivo (ver
  // IS_FIRST_APP_LAUNCH arriba) — BaulRoute lo usa junto al cooldown para no proponer la
  // recomendación de contribución antes de que la persona haya visto el resto de la app.
  isFirstAppLaunch: boolean;

  // Fotos para las que este dispositivo ya ha enviado una solicitud de retirada (ver
  // usePhotoViewerActions). Cliente-only, nunca en backend: leer el estado de una solicitud
  // (GET /removal-requests) exige AccessLevel.Admin, así que un colaborador normal no tiene
  // otra forma de saber si ya solicitó la retirada de una foto concreta. Persistido en
  // localStorage para sobrevivir a recargar/cerrar la app, pero no se limpia nunca — si el
  // custodio rechaza la solicitud, hoy no hay forma de volver a habilitar el botón desde aquí.
  removalRequestedPhotoIds: string[];
  hasRequestedPhotoRemoval: (photoId: string) => boolean;
  markPhotoRemovalRequested: (photoId: string) => void;

  // "Invitar a la familia" attention banner (Historia tab, see BaulFeedTabContainer): dismissing
  // it only hides it for the rest of this app session, reappearing the next time the app is
  // opened — so, unlike the cooldowns/flags above, this is plain in-memory state, never written
  // to localStorage. Keyed by baulId, same reasoning as the contribution-suggestion cooldown:
  // dismissing it in one baúl shouldn't hide it in another.
  dismissedInviteBannerBaulIds: string[];
  isInviteBannerDismissed: (baulId: string) => boolean;
  dismissInviteBanner: (baulId: string) => void;
}

export const useUIStore = create<UIState>((set, get) => ({
  // Toast
  showToast: false,
  toastMessage: '',
  toastVariant: 'success',
  showToastMessage: (message: string, variant: ToastVariant = 'success') => {
    set({ showToast: true, toastMessage: message, toastVariant: variant });
    // Auto-hide after 3 seconds
    setTimeout(() => {
      set({ showToast: false });
    }, 3000);
  },
  hideToast: () => set({ showToast: false }),

  isContributionSuggestionOnCooldown: (baulId) => {
    const dismissedAt = readContributionSuggestionCooldowns()[baulId];
    return dismissedAt !== undefined && Date.now() - dismissedAt < contributionSuggestionCooldownMs();
  },
  startContributionSuggestionCooldown: (baulId) => {
    const cooldowns = readContributionSuggestionCooldowns();
    const cooldownMs = contributionSuggestionCooldownMs();
    // Aprovecha la escritura para descartar entradas ya caducadas — sin esto, un baúl visitado
    // una vez se queda en localStorage para siempre.
    for (const [id, dismissedAt] of Object.entries(cooldowns)) {
      if (Date.now() - dismissedAt >= cooldownMs) delete cooldowns[id];
    }
    cooldowns[baulId] = Date.now();
    writeContributionSuggestionCooldowns(cooldowns);
  },

  isFirstAppLaunch: IS_FIRST_APP_LAUNCH,

  removalRequestedPhotoIds: readRemovalRequestedPhotoIds(),
  hasRequestedPhotoRemoval: (photoId) => get().removalRequestedPhotoIds.includes(photoId),
  markPhotoRemovalRequested: (photoId) => {
    if (get().removalRequestedPhotoIds.includes(photoId)) return;
    const ids = [...get().removalRequestedPhotoIds, photoId];
    writeRemovalRequestedPhotoIds(ids);
    set({ removalRequestedPhotoIds: ids });
  },

  dismissedInviteBannerBaulIds: [],
  isInviteBannerDismissed: (baulId) => get().dismissedInviteBannerBaulIds.includes(baulId),
  dismissInviteBanner: (baulId) => {
    if (get().dismissedInviteBannerBaulIds.includes(baulId)) return;
    set({ dismissedInviteBannerBaulIds: [...get().dismissedInviteBannerBaulIds, baulId] });
  },
}));
